/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Outlet, Navigate, useParams } from 'react-router-dom';
import { CurrencyProvider } from './contexts/CurrencyContext';
import { CartProvider } from './context/CartContext';
import { AuthProvider } from './context/AuthContext';
import { AuthGateProvider } from './context/AuthGateContext';
import { ProductRefreshProvider } from './admin/ProductRefreshContext';
import MainLayout from './components/MainLayout';
import AccountAreaLayout from './components/AccountAreaLayout';
import App from './App';
import {
  AdminLayout,
  AdminDashboard,
  ProductManagement,
  ProductAddWizard,
  ProductEditPage,
  CategoryManagement,
  UserManagement,
  OrderManagement,
  BillUpload,
  AdminSettingsPage,
  BannerManagerSimple,
  CouponManager,
  EmailCampaignsManager,
  BrandsManagement,
  HeaderButtonManager,
  SalesReport,
  OrderReport,
  CustomerReport,
  ProductReport,
  TopSellingProducts,
  ProductReviewsManagement,
  ProductImport,
  CustomerBills,
  ShippingSettings,
  ShippingManagement,
  ShippingIntegration,
  DatabaseBackup,
  ErrorLogs,
  CacheClear,
  MaintenanceMode,
  HeaderManager,
  HeaderMenuManager,
  UltraSettings,
  AdminAccountSettings,
  ProductPriceManager,
  NavigationCategoriesManager,
  FooterNavigationManager,
  HomepageControl,
  ProductSectionsManager,
  FeaturedProductsManager,
  TopSellingManager,
  NewArrivalsManager,
  TrendingProductsManager,
  HomepageDealsSettings,
  DealCampaignManager,
  BrandShowcaseManager,
  PromotionalBannerManager,
  SplitBannerManager,
  CategoryProductsManager,
  RecentlyViewedManager,
  RecommendedProductsManager,
  FlashSaleManager,
  FlashSaleCreate,
  PopularProductsManager,
  NewsletterManager,
  NewsletterSubscribers,
  ContactPageManager,
  ContactInbox,
  InfoPageManager,
  EmailSmtpSettings,
  AppDownloadManager,
  TestimonialsManager,
  MarketplaceCompareSettings,
  OfferBannerPopupManager,
} from './admin/routeLazy';
import Login from './pages/Login';
import AdminLogin from './pages/AdminLogin';
import Register from './pages/Register';
import AdminRoute from './components/AdminRoute';
import ProtectedRoute from './components/ProtectedRoute';
import CategoryPage from './pages/CategoryPage';
import CategoriesPage from './pages/CategoriesPage';
import ProductsPage from './pages/ProductsPage';
import ProductDetailPageAmazon from './pages/ProductDetailPageAmazon';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import SearchPage from './pages/SearchPage';
import WishlistPage from './pages/WishlistPage';
import AccountPage from './pages/AccountPage';
import OrdersPage from './pages/OrdersPage';
import SettingsPage from './pages/SettingsPage';
import AddCardPage from './pages/AddCardPage';
import LogoutPage from './pages/LogoutPage';
import TrackOrder from './pages/TrackOrder';
import About from './pages/About';
import ContactUs from './pages/ContactUs';
import SalePage from './pages/SalePage';
import GenericPage from './pages/GenericPage';
import Brands from './pages/Brands';
import BrandProducts from './pages/BrandProducts';
import DealsPage from './pages/DealsPage';
import DealDetailPage from './pages/DealDetailPage';

import ShippingInfo from './pages/ShippingInfo';
import SizeGuide from './pages/SizeGuide';
import FAQ from './pages/FAQ';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Legal from './pages/Legal';
import TermsOfService from './pages/TermsOfService';
import CookiePolicy from './pages/CookiePolicy';
import TermsOfSale from './pages/TermsOfSale';
import NewsletterUnsubscribe from './pages/NewsletterUnsubscribe';

// User Dashboard Pages
import UserDashboard from './pages/UserDashboard';
import UserProfile from './pages/UserProfile';
import AddressManagement from './pages/AddressManagement';
import UserCoupons from './pages/UserCoupons';
import Notifications from './pages/Notifications';
import OrderDetail from './pages/OrderDetail';
import MyBills from './pages/MyBills';

// Redirect component for old brand URLs
const BrandRedirect = () => {
  const { brandName } = useParams<{ brandName: string }>();
  return <Navigate to={`/brands/${brandName}`} replace />;
};

const adminSuspenseFallback = (
  <div className="flex min-h-[50vh] items-center justify-center bg-slate-50 text-slate-500">
    Loading admin…
  </div>
);

const Router = () => {
  return (
    <AuthProvider>
      <CurrencyProvider>
        <CartProvider>
          <BrowserRouter>
            <AuthGateProvider>
            <Routes>
            {/* Public Routes - Wrapped with MainLayout for bottom nav */}
            <Route element={<MainLayout><Outlet /></MainLayout>}>
              <Route path="/" element={<App />} />
            <Route path="/home" element={<App />} />
            <Route path="/index.html" element={<App />} />
            
            {/* Category Routes - Dynamic */}
            <Route path="/category/:slug" element={<CategoryPage />} />
            
            {/* Product Routes */}
            <Route path="/products" element={<ProductsPage />} />
            <Route path="/shop" element={<ProductsPage />} />
            <Route path="/product/:slug" element={<ProductDetailPageAmazon />} />
            <Route path="/products/:slug" element={<ProductDetailPageAmazon />} />
            <Route path="/professional-product/:slug" element={<ProductDetailPageAmazon />} />
            
            {/* Shopping Routes */}
            <Route path="/cart" element={<ProtectedRoute><CartPage /></ProtectedRoute>} />
            <Route path="/checkout" element={<ProtectedRoute><CheckoutPage /></ProtectedRoute>} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/wishlist" element={<ProtectedRoute><WishlistPage /></ProtectedRoute>} />
            <Route path="/track-order" element={<TrackOrder />} />
            
            {/* Info Pages */}
            <Route path="/shipping" element={<ShippingInfo />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/size-guide" element={<SizeGuide />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />
            <Route path="/terms-of-sale" element={<TermsOfSale />} />
            <Route path="/categories" element={<CategoriesPage />} />
            <Route path="/brands" element={<Brands />} />
            <Route path="/brands/:slug" element={<BrandProducts />} />
            <Route path="/brand/:brandName" element={<BrandRedirect />} />
            <Route path="/about" element={<About />} />
            <Route path="/legal" element={<Legal />} />
            <Route path="/new-arrivals" element={<ProductsPage variant="new-arrivals" />} />
            <Route path="/contact" element={<ContactUs />} />
            <Route path="/newsletter/unsubscribe" element={<NewsletterUnsubscribe />} />
            <Route path="/sale" element={<SalePage />} />
            <Route path="/deals" element={<DealsPage />} />
            <Route path="/deals/:slug" element={<DealDetailPage />} />

            {/* Customer account — protected, store header/footer */}
            <Route element={<AccountAreaLayout />}>
              <Route path="/dashboard" element={<UserDashboard />} />
              <Route path="/profile" element={<UserProfile />} />
              <Route path="/addresses" element={<AddressManagement />} />
              <Route path="/coupons" element={<UserCoupons />} />
              <Route path="/notifications" element={<Notifications />} />
              <Route path="/payments" element={<Navigate to="/dashboard" replace />} />
              <Route path="/wallet" element={<Navigate to="/dashboard" replace />} />
              <Route path="/my-bills" element={<MyBills />} />
              <Route path="/orders" element={<OrdersPage />} />
              <Route path="/orders/:orderId" element={<OrderDetail />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/account" element={<AccountPage />} />
            </Route>
          </Route>
          
          {/* Admin Routes - SECURE HIDDEN PATH */}
          <Route
            path="/system-control-panel-7x9k2m/*"
            element={
              <AdminRoute>
                <ProductRefreshProvider>
                  <Suspense fallback={adminSuspenseFallback}>
                    <AdminLayout />
                  </Suspense>
                </ProductRefreshProvider>
              </AdminRoute>
            }
          >
            <Route index element={<AdminDashboard />} />
            <Route path="dashboard" element={<AdminDashboard />} />
            {/* Banner Manager - NEW SYSTEM */}
            <Route path="banners" element={<BannerManagerSimple />} />
            
            {/* Products */}
            <Route path="products" element={<ProductManagement />} />
            <Route path="products/add" element={<ProductAddWizard />} />
            <Route path="products/edit/:id" element={<ProductEditPage />} />
            <Route path="products/import" element={<ProductImport />} />
            <Route path="marketplace-compare" element={<MarketplaceCompareSettings />} />
            <Route path="categories" element={<CategoryManagement />} />
            <Route path="brands" element={<BrandsManagement />} />
          
          {/* Marketing */}
          <Route path="marketing/offer-popup" element={<OfferBannerPopupManager />} />
          <Route path="coupons" element={<CouponManager />} />
          <Route path="newsletter-subscribers" element={<NewsletterSubscribers />} />
          <Route path="contact-page" element={<ContactPageManager />} />
          <Route path="contact-messages" element={<ContactInbox />} />
          <Route path="about-page" element={<InfoPageManager />} />
          <Route path="privacy-page" element={<InfoPageManager />} />
          <Route path="legal-page" element={<InfoPageManager />} />
          <Route path="email-smtp" element={<EmailSmtpSettings />} />
          <Route path="email-campaigns" element={<EmailCampaignsManager />} />
          
          {/* Orders */}
            <Route path="orders" element={<OrderManagement />} />
            <Route path="bill-upload" element={<BillUpload />} />
            <Route path="shipping-dashboard" element={<ShippingManagement />} />
            <Route path="shipping-integration" element={<ShippingIntegration />} />
          
          {/* Customers */}
          <Route path="customers" element={<UserManagement />} />
          <Route path="customer-bills" element={<CustomerBills />} />
          
          {/* Appearance */}
          <Route path="header" element={<HeaderManager />} />
          <Route path="footer" element={<AdminSettingsPage />} />
          <Route path="shipping" element={<ShippingSettings />} />
          <Route path="ultra-settings" element={<UltraSettings />} />
          
          {/* Admin Account Settings */}
          <Route path="settings/account" element={<AdminAccountSettings />} />
          <Route path="header-buttons" element={<HeaderButtonManager />} />

          {/* Product Price Manager */}
          <Route path="product-prices" element={<ProductPriceManager />} />
          
          {/* New Admin Modules */}
          {/* Reports */}
          <Route path="reports" element={<SalesReport />} />
          <Route path="reports/sales" element={<SalesReport />} />
          <Route path="reports/orders" element={<OrderReport />} />
          <Route path="reports/customers" element={<CustomerReport />} />
          <Route path="reports/products" element={<ProductReport />} />
          <Route path="reports/revenue" element={<SalesReport />} />
          <Route path="reports/top-products" element={<TopSellingProducts />} />
          
          {/* Reviews */}
          <Route path="reviews" element={<ProductReviewsManagement />} />
          <Route path="reviews/ratings" element={<ProductReviewsManagement />} />
          <Route path="reviews/pending" element={<ProductReviewsManagement />} />
          
          {/* Homepage Control */}
            <Route path="homepage-control" element={<HomepageControl />} />
            <Route path="product-sections" element={<ProductSectionsManager />} />
            <Route path="homepage-control/featured" element={<FeaturedProductsManager />} />
            <Route path="homepage-control/top-selling" element={<TopSellingManager />} />
            <Route path="homepage-control/new-arrivals" element={<NewArrivalsManager />} />
            <Route path="homepage-control/trending" element={<TrendingProductsManager />} />
            <Route path="deals" element={<DealCampaignManager />} />
            <Route path="homepage-control/deals" element={<HomepageDealsSettings />} />
            <Route path="homepage-control/brands" element={<BrandShowcaseManager />} />
            <Route path="homepage-control/banner" element={<PromotionalBannerManager />} />
            <Route path="homepage-control/split-banner" element={<SplitBannerManager />} />
            <Route path="homepage-control/category-products" element={<CategoryProductsManager />} />
            <Route path="homepage-control/recently-viewed" element={<RecentlyViewedManager />} />
            <Route path="homepage-control/recommended" element={<RecommendedProductsManager />} />
            <Route path="homepage-control/flash-sale" element={<FlashSaleManager />} />
            <Route path="flash-sales/create" element={<FlashSaleCreate />} />
            <Route path="homepage-control/popular" element={<PopularProductsManager />} />
            <Route path="homepage-control/newsletter" element={<NewsletterManager />} />
            <Route path="homepage-control/app-download" element={<AppDownloadManager />} />
            <Route path="homepage-control/testimonials" element={<TestimonialsManager />} />
          
          {/* Admin Users */}
          <Route path="admin-users" element={<UserManagement />} />
          <Route path="admin-users/roles" element={<UserManagement />} />
          <Route path="admin-users/permissions" element={<UserManagement />} />
          
          {/* System Tools */}
          <Route path="system/backup" element={<DatabaseBackup />} />
          <Route path="system/logs" element={<ErrorLogs />} />
          <Route path="system/cache" element={<CacheClear />} />
          <Route path="system/maintenance" element={<MaintenanceMode />} />
          
          {/* Navigation (live) */}
          <Route path="navigation/categories" element={<NavigationCategoriesManager />} />
          <Route path="navigation/footer" element={<FooterNavigationManager />} />
          <Route path="appearance/header-menu" element={<HeaderMenuManager />} />

          {/* Legacy / demo paths → real modules */}
          <Route path="customer-orders" element={<Navigate to="/system-control-panel-7x9k2m/orders" replace />} />
          <Route path="cms/*" element={<Navigate to="/system-control-panel-7x9k2m/dashboard" replace />} />
          <Route path="blog/*" element={<Navigate to="/system-control-panel-7x9k2m/dashboard" replace />} />
          <Route path="navigation" element={<Navigate to="/system-control-panel-7x9k2m/navigation/categories" replace />} />
          <Route path="appearance/footer" element={<Navigate to="/system-control-panel-7x9k2m/navigation/footer" replace />} />
          <Route path="appearance/navigation" element={<Navigate to="/system-control-panel-7x9k2m/navigation/categories" replace />} />
          <Route path="appearance/header" element={<Navigate to="/system-control-panel-7x9k2m/header" replace />} />
          <Route path="notifications/*" element={<Navigate to="/system-control-panel-7x9k2m/orders" replace />} />
          <Route path="system/system-logs" element={<Navigate to="/system-control-panel-7x9k2m/system/logs" replace />} />
          <Route path="users" element={<Navigate to="/system-control-panel-7x9k2m/customers" replace />} />
        </Route>
        
        {/* Auth Routes - No bottom nav */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<GenericPage title="Forgot Password" description="Reset your password." />} />
        <Route path="/logout" element={<LogoutPage />} />
        <Route path="/aadcard" element={<AddCardPage />} />
        
        {/* Admin Auth - No bottom nav */}
        <Route path="/system-access-9x4m2k" element={<AdminLogin />} />
      </Routes>
            </AuthGateProvider>
          </BrowserRouter>
        </CartProvider>
      </CurrencyProvider>
    </AuthProvider>
  );
};

export default Router;
